import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ExternalLinkIcon from "../../../../assets/images/icons/external-link.svg";
import PointerClickIcon from "../../../../assets/images/icons/pointer-click.svg";
import TopCountryVisitChart from "./TopCountryVisitChart";
import { AnalyticsChart } from "./AnalyticsChart";
import { CollectedDataTable } from "../components/CollectedDataTable";
import { Demography } from "./Demography"
import { STREAM_ICONS } from "../constants/allPlatformIcon";
import AppTour from "../../../../components/AppTour/AppTour";
import { linkAnalyticsTourSteps } from "../../../../components/AppTour/ModuleTourSteps";

const DEFAULT_STREAMS = [
    { name: "spotify", clicksToService: 0 },
    { name: "youtube", clicksToService: 0 }
];

const normalizeName = (name = "") =>
    name
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]/g, "_")
        .replace(/_+/g, "_");

const STREAM_UI = {
    spotify: { label: "Spotify", icon: STREAM_ICONS.spotify },
    youtube: { label: "YouTube", icon: STREAM_ICONS.youtube },
    apple_music: { label: "Apple Music", icon: STREAM_ICONS.apple_music },
    instagram: { label: "Instagram", icon: STREAM_ICONS.instagram },
};

const DUMMY_TIMELINE = [
    { date: "2025-01-01", clicks: 120, services: 45 },
    { date: "2025-01-02", clicks: 150, services: 65 },
    { date: "2025-01-03", clicks: 180, services: 78 },
    { date: "2025-01-04", clicks: 200, services: 92 },
    { date: "2025-01-05", clicks: 220, services: 105 },
    { date: "2025-01-06", clicks: 240, services: 118 },
    { date: "2025-01-07", clicks: 210, services: 95 },
    { date: "2025-01-08", clicks: 260, services: 135 },
    { date: "2025-01-09", clicks: 280, services: 155 },
    { date: "2025-01-10", clicks: 300, services: 175 },
    { date: "2025-01-11", clicks: 320, services: 192 },
    { date: "2025-01-12", clicks: 290, services: 168 },
    { date: "2025-01-13", clicks: 310, services: 185 },
    { date: "2025-01-14", clicks: 340, services: 210 },
];

const DUMMY_STREAMS = [
    { name: "spotify", clicksToService: 450 },
    { name: "youtube", clicksToService: 380 },
    { name: "apple_music", clicksToService: 290 },
    { name: "instagram", clicksToService: 200 },
];

const DUMMY_LOCATIONS = [
    { country: "United States", city: "New York", clicks: 520, flag: "🇺🇸" },
    { country: "United Kingdom", city: "London", clicks: 380, flag: "🇬🇧" },
    { country: "Canada", city: "Toronto", clicks: 290, flag: "🇨🇦" },
    { country: "Australia", city: "Sydney", clicks: 210, flag: "🇦🇺" },
    { country: "India", city: "Mumbai", clicks: 395, flag: "🇮🇳" },
];

const DUMMY_AUDIENCE = [
    {
        id: 1,
        displayName: "John Doe",
        email: "john.doe@example.com",
        country: "United States",
        city: "New York",
        clicks: 15,
        date: "2025-01-14",
    },
    {
        id: 2,
        displayName: "Jane Smith",
        email: "jane.smith@example.com",
        country: "United Kingdom",
        city: "London",
        clicks: 12,
        date: "2025-01-13",
    },
    {
        id: 3,
        displayName: "Michael Johnson",
        email: "michael.j@example.com",
        country: "Canada",
        city: "Toronto",
        clicks: 8,
        date: "2025-01-12",
    },
    {
        id: 4,
        displayName: "Sarah Wilson",
        email: "sarah.wilson@example.com",
        country: "Australia",
        city: "Sydney",
        clicks: 10,
        date: "2025-01-14",
    },
    {
        id: 5,
        displayName: "Rajesh Kumar",
        email: "rajesh.k@example.com",
        country: "India",
        city: "Mumbai",
        clicks: 18,
        date: "2025-01-10",
    },
    {
        id: 6,
        displayName: "Emma Brown",
        email: "emma.brown@example.com",
        country: "United States",
        city: "Los Angeles",
        clicks: 22,
        date: "2025-01-14",
    },
    {
        id: 7,
        displayName: "David Martinez",
        email: "david.m@example.com",
        country: "United States",
        city: "Chicago",
        clicks: 14,
        date: "2025-01-09",
    },
    {
        id: 8,
        displayName: "Lisa Anderson",
        email: "lisa.anderson@example.com",
        country: "Canada",
        city: "Vancouver",
        clicks: 16,
        date: "2025-01-11",
    },
];

const getStreamIcon = (rawName) => {
    const key = normalizeName(rawName);
    return STREAM_ICONS[key] || STREAM_ICONS[key.replace("_music", "")] || ExternalLinkIcon;
};

export default function LinkAnalyticsDemo() {
    // Bottom Tabs Section Design
    const { hash } = useLocation();
    const [runTour, setRunTour] = useState(false);
    const [activeTab, setActiveTab] = useState('cities');
    const [selectedTab, setSelectedTab] = useState("contentAnalytics");
    const [analyticsRange, setAnalyticsRange] = useState("all-time");

    useEffect(() => {
        if (hash === "#start-tour") {
            setRunTour(true);
        }
    }, [hash]);

    const tabsMenu = [
        { id: "contentAnalytics", label: "Content Analytics" },
        { id: "demographics", label: "Demographics" },
        { id: "collectedData", label: "Collected Data" },
    ];

    const [showPageViews, setShowPageViews] = useState(true);
    const [showServiceClicks, setShowServiceClicks] = useState(true);

    const TAB_STEP_MAP = {
        5: "contentAnalytics",
        7: "demographics",
        9: "collectedData",
    };

    const handleTourCallback = (data) => {
        const { type, index, action, status } = data;

        if (status === "finished" || status === "skipped") {
            setRunTour(false);
            return;
        }

        if (type === "step:after") {
            const nextIndex = action === "prev" ? index - 1 : index + 1;
            if (TAB_STEP_MAP[nextIndex] !== undefined) {
                setSelectedTab(TAB_STEP_MAP[nextIndex]);
            }
        }
    };

    // Mock data
    const smartLinkAnalytics = {
        totals: {
            clicks: 3640,
            clicksToService: 1895,
            audience: DUMMY_AUDIENCE,
        },
        timeline: DUMMY_TIMELINE,
        streams: DUMMY_STREAMS,
        locations: DUMMY_LOCATIONS,
        audience: DUMMY_AUDIENCE,
    };

    const analyticsLoading = false;
    const type = "musiclink";

    let finalStreams = [];
    if (smartLinkAnalytics?.streams && smartLinkAnalytics?.streams.length > 0) {
        const normalized = smartLinkAnalytics?.streams.map(s => ({
            ...s,
            name: normalizeName(s.name),
            clicksToService: s.clicksToService || 0
        }));
        finalStreams = [...normalized];
    } else {
        finalStreams = [...DEFAULT_STREAMS]
    }

    const handleRangeChange = (range) => {
        // Just for demo
    };

    return (
        <div className="">
            <AppTour
                tourSteps={linkAnalyticsTourSteps}
                runTour={runTour}
                setRunTour={setRunTour}
                callback={handleTourCallback}
            />

            <div className="heading-wrapper mb-4" data-tour="high-level-stats">
                <div className="font-bold text-theme_clr1 text-lg">High-Level Stats</div>
                <div className="font-bold text-theme_clr1/70 text-base mb-4">See how your website is performing</div>
            </div>

            <div className="select-wrapper mb-5">
                <select
                    data-tour="range-selector"
                    className="tw-form-control font-semibold !w-auto !pr-8"
                    value={analyticsRange}
                    onChange={(e) => handleRangeChange(e.target.value)}
                >
                    <option value="all-time">All Time</option>
                    <option value="weekly">Last 7 Days</option>
                    <option value="monthly">Last 30 Days</option>
                    <option value="yearly">Last 12 Months</option>
                </select>
            </div>

            {/* Stats Card Grid Section */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-4" data-tour="stats-grid">
                <div className="border border-theme_clr1 rounded-lg p-4" data-tour="page-views-card">
                    <div className="icon-cover mb-2">
                        <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                            <img src={PointerClickIcon} alt="Pointer Click Icon" />
                        </span>
                    </div>
                    <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Page Views</div>
                    <div className="text-xl font-bold text-theme_clr1">
                        {smartLinkAnalytics?.totals?.clicks || 0}
                    </div>
                </div>

                <div className="border border-theme_clr1 rounded-lg p-4" data-tour="click-to-service-card">
                    <div className="icon-cover mb-2">
                        <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                            <img src={PointerClickIcon} alt="Pointer Click Icon" />
                        </span>
                    </div>
                    <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Click to Service</div>
                    <div className="text-xl font-bold text-theme_clr1">
                        {smartLinkAnalytics?.totals?.clicksToService || 0}
                    </div>
                </div>

                <div className="border border-theme_clr1 rounded-lg p-4" data-tour="signups-card">
                    <div className="icon-cover mb-2">
                        <span className="h-[40px] w-[40px] inline-flex items-center justify-center bg-theme_clr2 bg-opacity-40 rounded-lg p-1">
                            <img src={PointerClickIcon} alt="Pointer Click Icon" />
                        </span>
                    </div>
                    <div className="font-bold text-sm text-theme_clr1 text-opacity-70">Signups</div>
                    <div className="text-xl font-bold text-theme_clr1">
                        {smartLinkAnalytics?.totals?.audience?.length || 0}
                    </div>
                </div>
            </div>

            {/* Chart Section */}
            <div className="chart-wrapper mb-4" data-tour="analytics-chart">
                <img
                    src={require('../../../../assets/images/link-analytics-chart.png')}
                    className="h-[400px] w-auto"
                />
                <div className="flex justify-center items-center" data-tour="chart-toggles">
                    <div className="space-x-4">
                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={showPageViews}
                                onChange={(e) => setShowPageViews(e.target.checked)}
                                className="form-checkbox text-theme_clr2"
                            />
                            <span className="font-bold ml-2 text-sm text-theme_clr1">
                                Page Views
                            </span>
                        </label>

                        <label className="inline-flex items-center">
                            <input
                                type="checkbox"
                                checked={showServiceClicks}
                                onChange={(e) => setShowServiceClicks(e.target.checked)}
                                className="form-checkbox text-theme_clr2"
                            />
                            <span className="font-bold ml-2 text-sm text-theme_clr2">
                                Clicks to service
                            </span>
                        </label>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-theme_clr1 rounded-lg p-4">
                <div className="tab-header border-b border-theme_clr2 dark:border-theme_clr2 mb-2 sticky top-0 z-10 bg-white" data-tour="tabs-header">
                    <div className="flex-1 overflow-auto">
                        <ul className="flex flex-nowrap gap-4 text-sm font-jakarta font-bold text-left overflow-auto whitespace-nowrap">
                            {tabsMenu.map((tab) => (
                                <li key={tab.id} className="inline-block" data-tour={tab.id === "contentAnalytics" ? "content-analytics-tab" : tab.id === "demographics" ? "demographics-tab" : "collected-data-tab"}>
                                    <button onClick={() => setSelectedTab(tab.id)}
                                        className={`inline-block py-2 px-1 border-b-2 transition-all duration-200 text-nowrap ${selectedTab === tab.id
                                            ? "text-theme_clr1 border-theme_clr4"
                                            : "text-theme_clr1 text-opacity-70 border-transparent"
                                            }`}>
                                        {tab.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="font-jakarta tab-body content-wrapper overflow-auto flex-1 min-h-0 py-2">
                    {selectedTab === "contentAnalytics" && (
                        <div className="content-wrapper" data-tour="streaming-platforms">
                            <div className="heading-wrapper font-bold mb-4">
                                <div className="heading text-lg text-theme_clr1">{type !== 'biolink' ? 'Streaming' : ''} Platforms</div>
                                <div className="subheading text-base text-theme_clr1/70">See how your website is performing by {type !== 'biolink' ? 'Streaming' : ''} platforms</div>
                            </div>

                            <div className="flex flex-col gap-3">
                                {finalStreams.map((platform, index) => {
                                    const totalClicks = finalStreams.reduce(
                                        (sum, p) => sum + (p.clicksToService || 0),
                                        0
                                    );

                                    const percentage =
                                        totalClicks > 0
                                            ? Math.round((platform.clicksToService / totalClicks) * 100)
                                            : 0;

                                    const icon = getStreamIcon(platform.name);
                                    const ui = STREAM_UI[platform.name] || {
                                        label: platform.name,
                                        icon: null,
                                    };

                                    return (
                                        <div
                                            key={platform.name}
                                            className="item-wrapper flex items-center justify-between gap-5"
                                            data-tour={index === 0 ? "platform-item" : ""}
                                        >
                                            <div className="flex items-center gap-2">
                                                {ui.icon && (
                                                    <img
                                                        src={ui.icon}
                                                        alt={ui.label}
                                                        className="w-[35px]"
                                                    />
                                                )}
                                                <span
                                                    className="font-bold text-base text-theme_clr1 capitalize"
                                                >
                                                    {ui.label}</span>
                                            </div>

                                            <div className="flex w-[calc(100%-170px)] gap-2 items-center">
                                                <div className="font-bold text-theme_clr1">
                                                    {platform.clicksToService}
                                                </div>
                                                <div className="progressbar flex-1 h-[30px] bg-theme_clr2/30 rounded-lg overflow-hidden">
                                                    <div
                                                        className="h-full bg-theme_clr2 rounded-lg transition-all duration-500"
                                                        style={{ width: `${percentage}%` }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    )}

                    {selectedTab === "demographics" && (
                        <div data-tour="top-countries">
                            <Demography activeTab={activeTab} setActiveTab={setActiveTab} />
                            <TopCountryVisitChart location={smartLinkAnalytics?.locations || []} totalClicks={smartLinkAnalytics?.totals?.clicks} />
                        </div>
                    )}

                    {selectedTab === "collectedData" && (
                        <div data-tour="audience-table">
                            <CollectedDataTable audience={DUMMY_AUDIENCE || []} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
